import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCurrentEpisode,
  getEpisodeLupa,
  listEpisodesForPatient,
  type EpisodeLupaPeriodResponse,
} from '../services/api';
import type {
  DeletedTaskResponse,
  EpisodeSummaryResponse,
  EpisodeTakeoverCandidate,
  TaskListResponse,
} from '../services/api/types.gen';
import {
  currentEpisodeToAddTaskTarget,
  type AddTaskTargetEpisode,
} from '../components/patient/patientDetailTaskConstants';
import { addEpisodeBlockedCopy } from '../components/patient/addEpisodeBlockedCopy';

/**
 * Episode loading and selection for the patient chart.
 *
 * Split in two because the page's two data hooks depend on each other: the task
 * hook needs the selected episode to scope its fetches, and the pieces below
 * that build the Add Task target need the tasks. A task row carries the
 * episode clinic when one exists; a task-less episode uses the patient's.
 * Everything that does NOT need the tasks lives in `usePatientDetailEpisodes`,
 * which the page calls first;
 * `useAddTaskTargetEpisode` is called after the task hook with both halves in
 * hand. Effect order inside each hook matches the order the page declared them
 * in before the split.
 */
export interface UsePatientDetailEpisodesParams {
  patientId: string | undefined;
  episodeIdFromUrl: string | null;
  /** Tracks the patient the page is currently on, for late-resolving fetches. */
  currentPatientIdRef: React.MutableRefObject<string | undefined>;
  /** False holds the selection's LUPA load back, as on a task detail URL. */
  lupaEnabled?: boolean;
}

export interface PatientDetailEpisodesApi {
  episodes: EpisodeSummaryResponse[];
  setEpisodes: React.Dispatch<React.SetStateAction<EpisodeSummaryResponse[]>>;
  canAddEpisode: boolean;
  /** Why Add Episode is disabled, worded for the chart; null when it is not. */
  addEpisodeBlockedMessage: string | null;
  /**
   * An imported period the External EMR built that Add Episode can offer to
   * take over instead of refusing; null when there is none.
   */
  takeoverCandidate: EpisodeTakeoverCandidate | null;
  /**
   * Which patient's episode fetch has finished, successfully or not. Anything
   * that decides what to *show* from the selected episode needs this: without
   * it, "no episode" and "not loaded yet" are the same value, and on the first
   * render after the patient changes `episodes` still holds the previous
   * patient's rows because the clearing effect below has not run yet.
   */
  episodesSettledPatientId: string | null;
  selectedEpisodeId: string | null;
  setSelectedEpisodeId: React.Dispatch<React.SetStateAction<string | null>>;
  effectiveSelectedEpisodeId: string | null;
  selectedEpisode: EpisodeSummaryResponse | null;
  previousEpisode: EpisodeSummaryResponse | null;
  otherEpisodes: EpisodeSummaryResponse[];
  lupaPeriods: EpisodeLupaPeriodResponse[];
  lupaLoading: boolean;
  /** True when the latest LUPA load failed for the selection on screen. */
  lupaLastLoadFailedRef: React.MutableRefObject<boolean>;
  loadEpisodes: () => Promise<void>;
  /**
   * Re-fetch LUPA thresholds for the selected episode. Silent skips the
   * loading flag so the strip does not flash empty beside a live visit list
   * after a task mutation (counts must stay in sync with that list).
   */
  fetchLupa: (options?: { silent?: boolean }) => Promise<void>;
}

export function usePatientDetailEpisodes({
  patientId,
  episodeIdFromUrl,
  currentPatientIdRef,
  lupaEnabled = true,
}: UsePatientDetailEpisodesParams): PatientDetailEpisodesApi {
  const [episodes, setEpisodes] = useState<EpisodeSummaryResponse[]>([]);
  const [canAddEpisode, setCanAddEpisode] = useState(false);
  // Why Add Episode is withheld, worded for the chart. A failed fetch gets its own
  // copy, so the chart never shows a blank disabled control.
  const [addEpisodeBlockedMessage, setAddEpisodeBlockedMessage] = useState<
    string | null
  >(null);
  const [takeoverCandidate, setTakeoverCandidate] =
    useState<EpisodeTakeoverCandidate | null>(null);
  const [episodesSettledPatientId, setEpisodesSettledPatientId] = useState<
    string | null
  >(null);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(
    null
  );
  // Kept apart from `lupaPeriods.length`: an episode with no determinable
  // thresholds and an episode still fetching them both read as empty, and only
  // one of them should hold the strip's space open.
  const [lupaLoading, setLupaLoading] = useState(false);
  const [lupaPeriods, setLupaPeriods] = useState<EpisodeLupaPeriodResponse[]>(
    []
  );
  const previousPatientIdRef = useRef(patientId);

  // Clear patient-scoped state before fetching so stale episodes from the
  // previous patient cannot bleed into PatientHeader. Declared before the fetch
  // below, matching the order the page ran these in.
  useEffect(() => {
    if (!patientId) return;
    setEpisodes([]);
    setCanAddEpisode(false);
    setAddEpisodeBlockedMessage(null);
    setTakeoverCandidate(null);
  }, [patientId]);

  const loadEpisodes = useCallback(async () => {
    if (!patientId) return;
    const requestedPatientId = patientId;

    try {
      const response = await listEpisodesForPatient({
        path: { patient_id: requestedPatientId },
      });
      if (currentPatientIdRef.current !== requestedPatientId) {
        return;
      }
      setEpisodes(response.data?.episodes ?? []);
      setCanAddEpisode(response.data?.can_add_episode ?? false);
      setTakeoverCandidate(response.data?.takeover_candidate ?? null);
      setAddEpisodeBlockedMessage(
        addEpisodeBlockedCopy(
          response.data?.add_episode_blocked_reason ?? null,
          response.data?.latest_admission_discharge_date ?? null
        )
      );
    } catch (err) {
      if (currentPatientIdRef.current !== requestedPatientId) {
        return;
      }
      console.error('Error fetching episodes:', err);
      setEpisodes([]);
      setCanAddEpisode(false);
      setTakeoverCandidate(null);
      setAddEpisodeBlockedMessage('Unable to load episodes');
    } finally {
      if (currentPatientIdRef.current === requestedPatientId) {
        setEpisodesSettledPatientId(requestedPatientId);
      }
    }
  }, [currentPatientIdRef, patientId]);

  useEffect(() => {
    void loadEpisodes();
  }, [loadEpisodes]);

  useEffect(() => {
    if (!episodeIdFromUrl || episodes.length === 0) {
      return;
    }
    if (episodes.some(episode => episode.id === episodeIdFromUrl)) {
      setSelectedEpisodeId(episodeIdFromUrl);
    }
  }, [episodeIdFromUrl, episodes]);

  useEffect(() => {
    setEpisodesSettledPatientId(null);
  }, [patientId]);

  // Declared after the URL sync above so a patient switch always lands on the
  // newest episode rather than one the previous URL named.
  useEffect(() => {
    if (previousPatientIdRef.current === patientId) {
      return;
    }
    previousPatientIdRef.current = patientId;
    setSelectedEpisodeId(null);
  }, [patientId]);

  const effectiveSelectedEpisodeId = useMemo(() => {
    if (!episodes.length) return null;
    if (selectedEpisodeId && episodes.find(e => e.id === selectedEpisodeId))
      return selectedEpisodeId;
    return episodes[0].id;
  }, [episodes, selectedEpisodeId]);

  const selectedEpisode = useMemo(
    () => episodes.find(e => e.id === effectiveSelectedEpisodeId) ?? null,
    [episodes, effectiveSelectedEpisodeId]
  );

  // Selection identity for LUPA fetches. Request numbers alone are not enough:
  // switching to a patient with no episodes returns before bumping the
  // generation, and a delayed mutation can call a captured fetchLupa for
  // episode A after the chart has moved to B.
  const lupaRequestIdRef = useRef(0);
  // The latest LUPA load failed, so the next task refresh has to repair it.
  const lupaLastLoadFailedRef = useRef(false);
  const lupaEpisodeIdRef = useRef<string | null>(effectiveSelectedEpisodeId);
  lupaEpisodeIdRef.current = effectiveSelectedEpisodeId;

  const fetchLupa = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      // Always read the live selection. A delete/create handler closed over an
      // older fetchLupa must still refresh whatever episode is on screen now —
      // and must not apply a response for an episode the user already left.
      const episodeId = lupaEpisodeIdRef.current;
      const patientIdAtStart = currentPatientIdRef.current;
      // Invalidate every prior in-flight result, including the clear path when
      // the selection becomes null (no episodes / patient switch).
      const requestId = lupaRequestIdRef.current + 1;
      lupaRequestIdRef.current = requestId;
      lupaLastLoadFailedRef.current = false;

      if (!episodeId) {
        setLupaPeriods([]);
        setLupaLoading(false);
        return;
      }

      if (!silent) {
        setLupaLoading(true);
      }
      try {
        const response = await getEpisodeLupa({
          path: { episode_id: episodeId },
        });
        if (response.error) throw response.error;
        if (lupaRequestIdRef.current !== requestId) {
          return;
        }
        if (
          lupaEpisodeIdRef.current !== episodeId ||
          currentPatientIdRef.current !== patientIdAtStart
        ) {
          return;
        }
        setLupaPeriods(response.data?.periods ?? []);
      } catch (error) {
        // Non-blocking: the supplementary LUPA strip just stays hidden / stale.
        console.error('Failed to load LUPA thresholds', error);
        if (lupaRequestIdRef.current !== requestId) {
          return;
        }
        if (
          lupaEpisodeIdRef.current !== episodeId ||
          currentPatientIdRef.current !== patientIdAtStart
        ) {
          return;
        }
        lupaLastLoadFailedRef.current = true;
        if (!silent) {
          setLupaPeriods([]);
        }
      } finally {
        // Always clear for the latest request: a silent refresh can supersede a
        // non-silent one that set lupaLoading, and must take over clearing it.
        if (lupaRequestIdRef.current === requestId) {
          setLupaLoading(false);
        }
      }
    },
    [currentPatientIdRef]
  );

  // per-30-day-period LUPA thresholds for the selected episode.
  // Re-run on patient or episode change (including null) so a pending response
  // for the previous selection cannot repopulate a cleared gauge.
  // While disabled it waits, and a selection already loaded is not cleared.
  const lupaLoadedSelectionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lupaEnabled) return;
    const selection = `${patientId}|${effectiveSelectedEpisodeId}`;
    if (lupaLoadedSelectionRef.current === selection) return;
    lupaLoadedSelectionRef.current = selection;
    setLupaPeriods([]);
    void fetchLupa();
  }, [effectiveSelectedEpisodeId, patientId, fetchLupa, lupaEnabled]);

  const previousEpisode = useMemo(() => {
    if (episodes.length === 0) return null;
    return episodes.reduce((latest, episode) =>
      latest.end_date > episode.end_date ? latest : episode
    );
  }, [episodes]);

  // All episodes other than the currently selected one, in the same
  // newest-first order as `episodes`. Surfaced behind a subtle "View other
  // episodes" dropdown since switching episodes is rare.
  const otherEpisodes = useMemo(
    () => episodes.filter(e => e.id !== effectiveSelectedEpisodeId),
    [episodes, effectiveSelectedEpisodeId]
  );

  return {
    episodes,
    setEpisodes,
    canAddEpisode,
    addEpisodeBlockedMessage,
    takeoverCandidate,
    episodesSettledPatientId,
    selectedEpisodeId,
    setSelectedEpisodeId,
    effectiveSelectedEpisodeId,
    selectedEpisode,
    previousEpisode,
    otherEpisodes,
    lupaPeriods,
    lupaLoading,
    loadEpisodes,
    lupaLastLoadFailedRef,
    fetchLupa,
  };
}

export interface UseAddTaskTargetEpisodeParams {
  patientId: string | undefined;
  episodeIdFromUrl: string | null;
  selectedVisitId: string | null;
  episodes: EpisodeSummaryResponse[];
  selectedEpisode: EpisodeSummaryResponse | null;
  effectiveSelectedEpisodeId: string | null;
  setSelectedEpisodeId: React.Dispatch<React.SetStateAction<string | null>>;
  tasks: TaskListResponse[];
  linkedDeletedTask: DeletedTaskResponse | null;
  visitsLoading: boolean;
  tasksPatientId: string | null;
  currentPatientIdRef: React.MutableRefObject<string | undefined>;
  /**
   * Hold the current-episode read. The page holds it until it knows the
   * patient, and for good on an External EMR shell: a shell has no chart, so
   * the read can only 404, and the shell page offers no Add Task.
   */
  holdCurrentEpisodeFallback?: boolean;
  // Patient clinic, used only after tasks load and no task on the episode
  // carries one. A new episode has no tasks, so this is its clinic.
  patientClinicId?: string | null;
}

export interface AddTaskTargetEpisodeApi {
  addTaskTargetEpisode: AddTaskTargetEpisode | null;
  addTaskTargetClinicId: string | undefined;
}

function addTaskTargetFromSummary(
  episode: EpisodeSummaryResponse,
  tasks: TaskListResponse[],
  patientClinicId: string | null,
  tasksSettled: boolean
): AddTaskTargetEpisode {
  const clinicFromTasks = tasks.find(
    task => task.episode_id === episode.id && task.agency_clinic_id
  )?.agency_clinic_id;
  const physicianFromTasks = tasks.find(
    task => task.episode_id === episode.id && task.episode_physician_id
  )?.episode_physician_id;
  return {
    id: episode.id,
    start_date: episode.start_date,
    end_date: episode.end_date,
    status: episode.status,
    is_closed: episode.is_closed,
    agency_clinic_id:
      clinicFromTasks ??
      (tasksSettled ? (patientClinicId ?? undefined) : undefined),
    episode_physician_id:
      episode.clinic_physician_id ?? physicianFromTasks ?? undefined,
  };
}

export function useAddTaskTargetEpisode({
  patientId,
  episodeIdFromUrl,
  selectedVisitId,
  episodes,
  selectedEpisode,
  effectiveSelectedEpisodeId,
  setSelectedEpisodeId,
  tasks,
  linkedDeletedTask,
  visitsLoading,
  tasksPatientId,
  currentPatientIdRef,
  holdCurrentEpisodeFallback = false,
  patientClinicId = null,
}: UseAddTaskTargetEpisodeParams): AddTaskTargetEpisodeApi {
  const [currentEpisodeFallbackState, setCurrentEpisodeFallbackState] =
    useState<{
      patientId: string;
      episode: AddTaskTargetEpisode | null;
    } | null>(null);
  // The last visit id the episode header was moved onto. Kept so the move
  // happens once per visit: without it the effect could not tell its own
  // pinned episode from one the user picked, and would either fight the
  // header dropdown or refuse to follow a second visit in the same mount.
  const followedVisitEpisodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!patientId) return;
    setCurrentEpisodeFallbackState(null);
  }, [patientId]);

  // A visit deep link carries a task id and no episode, so the page would sit
  // on the newest episode while the URL names a visit in an older one: closing
  // the visit drops the user into an episode they never asked for, and the
  // header's insurance and authorization context describes the wrong one.
  // Move the header onto the visit's episode instead. Once per visit, so that
  // switching the header afterwards is not snapped back, and never over an
  // episode the URL named itself.
  useEffect(() => {
    if (episodeIdFromUrl || !selectedVisitId) return;
    if (followedVisitEpisodeIdRef.current === selectedVisitId) return;
    const linkedTask =
      tasks.find(task => task.id === selectedVisitId) ??
      (linkedDeletedTask?.id === selectedVisitId ? linkedDeletedTask : null);
    // Not yet resolvable: the tasks or the episodes are still loading, so leave
    // the ref alone and follow the visit once they arrive.
    if (!linkedTask) return;
    if (!episodes.some(episode => episode.id === linkedTask.episode_id)) return;
    followedVisitEpisodeIdRef.current = selectedVisitId;
    // Already the episode on screen. Pinning it would only stop the header
    // following the episode list, which matters when one is added.
    if (linkedTask.episode_id === effectiveSelectedEpisodeId) return;
    setSelectedEpisodeId(linkedTask.episode_id);
  }, [
    episodeIdFromUrl,
    effectiveSelectedEpisodeId,
    selectedVisitId,
    tasks,
    linkedDeletedTask,
    episodes,
    setSelectedEpisodeId,
  ]);

  const currentEpisodeFallback =
    currentEpisodeFallbackState &&
    currentEpisodeFallbackState.patientId === patientId
      ? currentEpisodeFallbackState.episode
      : null;

  useEffect(() => {
    let cancelled = false;
    const requestedPatientId = patientId;

    const loadCurrentEpisodeFallback = async () => {
      if (
        !requestedPatientId ||
        holdCurrentEpisodeFallback ||
        visitsLoading ||
        tasksPatientId !== requestedPatientId
      ) {
        return;
      }
      if (episodes.length > 0) {
        setCurrentEpisodeFallbackState(null);
        return;
      }

      try {
        const response = await getCurrentEpisode({
          path: { patient_id: requestedPatientId },
        });
        if (!cancelled && currentPatientIdRef.current === requestedPatientId) {
          setCurrentEpisodeFallbackState({
            patientId: requestedPatientId,
            episode: currentEpisodeToAddTaskTarget(response.data ?? null),
          });
        }
      } catch (error) {
        console.error(
          'Failed to load current episode for Add Task bounds:',
          error
        );
        if (!cancelled && currentPatientIdRef.current === requestedPatientId) {
          setCurrentEpisodeFallbackState({
            patientId: requestedPatientId,
            episode: null,
          });
        }
      }
    };

    void loadCurrentEpisodeFallback();

    return () => {
      cancelled = true;
    };
  }, [
    currentPatientIdRef,
    episodes.length,
    holdCurrentEpisodeFallback,
    patientId,
    tasksPatientId,
    visitsLoading,
  ]);

  const taskDerivedLatestEpisode = episodes[0] ?? null;
  // A list still in flight is not "this episode has no tasks".
  const tasksSettled = Boolean(patientId) && tasksPatientId === patientId;

  const addTaskTargetEpisode = useMemo<AddTaskTargetEpisode | null>(() => {
    if (selectedEpisode) {
      return addTaskTargetFromSummary(
        selectedEpisode,
        tasks,
        patientClinicId,
        tasksSettled
      );
    }
    if (taskDerivedLatestEpisode) {
      return addTaskTargetFromSummary(
        taskDerivedLatestEpisode,
        tasks,
        patientClinicId,
        tasksSettled
      );
    }
    return currentEpisodeFallback;
  }, [
    currentEpisodeFallback,
    patientClinicId,
    selectedEpisode,
    taskDerivedLatestEpisode,
    tasks,
    tasksSettled,
  ]);

  return {
    addTaskTargetEpisode,
    addTaskTargetClinicId: addTaskTargetEpisode?.agency_clinic_id ?? undefined,
  };
}
